/**
 * Phase 3.6 of admin-managed-zebu-accounts: admin configures auto-login for
 * a managed user. Backend writes the api-credentials secret + flips
 * auto_login_enabled on the broker-accounts row.
 *
 * Trust model: admin holds the managed user's Zebu password + TOTP seed
 * with client consent. Inputs are masked; form state is scrubbed on close.
 */
import React, { useEffect, useState } from 'react';
import { AlertCircle, Eye, EyeOff, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import managedBrokerService from '../../services/managedBrokerService';

interface AutoLoginOnBehalfDialogProps {
  open: boolean;
  managedUserId: string;
  managedClientId: string;
  managedDisplayName: string;
  /** Whether auto-login is currently enabled — controls dialog framing
   *  ("Enable Auto-Login" vs "Update Credentials"). */
  currentlyEnabled: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AutoLoginOnBehalfDialog: React.FC<AutoLoginOnBehalfDialogProps> = ({
  open,
  managedUserId,
  managedClientId,
  managedDisplayName,
  currentlyEnabled,
  onClose,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [totpKey, setTotpKey] = useState('');
  const [autoLoginEnabled, setAutoLoginEnabled] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showTotpKey, setShowTotpKey] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Always start blank; admin types each time. Inputs are scrubbed on close.
      setPassword('');
      setTotpKey('');
      setAutoLoginEnabled(true);
      setShowPassword(false);
      setShowTotpKey(false);
      setError(null);
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) return;
    // Scrub form state — credentials never linger in memory beyond the dialog lifecycle.
    setPassword('');
    setTotpKey('');
    onClose();
  };

  const handleSubmit = async () => {
    // For enabling: require password + totp_key. For disabling: just need the toggle.
    if (autoLoginEnabled && (!password.trim() || !totpKey.trim())) {
      setError('Password and TOTP secret key are required when enabling auto-login');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const request: any = { auto_login_enabled: autoLoginEnabled };
      if (password.trim()) request.password = password.trim();
      if (totpKey.trim()) request.totp_key = totpKey.trim();
      await managedBrokerService.setAutoLoginOnBehalf(managedUserId, managedClientId, request);
      // Scrub before bubbling up
      setPassword('');
      setTotpKey('');
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to configure auto-login');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const title = currentlyEnabled ? 'Update Auto-Login Credentials' : 'Enable Auto-Login';

  return (
    <Modal isOpen={open} onClose={handleClose} maxWidth="md" ariaLabel={title}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              On behalf of <span className="font-medium">{managedDisplayName}</span> ({managedClientId})
            </p>
          </div>
          <button onClick={handleClose} disabled={isSubmitting} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}

        <div className="space-y-4">
          {/* Auto-login toggle */}
          <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable Auto-Login</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Automatically refresh Zebu session every morning at 08:05 IST
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAutoLoginEnabled(!autoLoginEnabled)}
              disabled={isSubmitting}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                autoLoginEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
              aria-label="Toggle auto-login"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoLoginEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {autoLoginEnabled && (
            <>
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Zebu Account Password *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    placeholder={currentlyEnabled ? 'Leave empty to keep current password' : 'Managed user\'s Zebu login password'}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>

              {/* TOTP secret key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  TOTP Secret Key *
                </label>
                <div className="relative">
                  <Input
                    type={showTotpKey ? 'text' : 'password'}
                    value={totpKey}
                    onChange={(e) => { setTotpKey(e.target.value); setError(null); }}
                    placeholder={currentlyEnabled ? 'Leave empty to keep current TOTP key' : 'Base32 seed from authenticator setup'}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowTotpKey(!showTotpKey)}
                  >
                    {showTotpKey ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  The base32 string from the managed user's authenticator-app setup — not the 6-digit OTP.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={isSubmitting}>
            {autoLoginEnabled ? (currentlyEnabled ? 'Update' : 'Enable') : 'Disable'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AutoLoginOnBehalfDialog;
